import { useState, useMemo, useEffect, useCallback } from 'react';
import { calculateReversePiti } from '@/utils/calculator/reversePitiCalculator';
import { LoanTypeOption } from '@/components/calculator/utils/types';
import { getDefaultValuesForLoanType } from '@/hooks/calculator/useLoanTypeDefaults';
import { useMarketData } from '@/contexts/MarketDataContext';

export type VAStatus = 'activeDuty' | 'separatedRetired' | null;
export type VADisabilityStatus = 'none' | 'partial' | 'full' | null;

// Round to 3 decimal places
const roundRate = (rate: number): number => Math.round(rate * 1000) / 1000;

export const useReversePitiState = () => {
  const { getInterestRateForLoanType, marketData } = useMarketData();
  
  // Form state
  const [loanType, setLoanType] = useState<LoanTypeOption>('conventional');
  const [vaStatus, setVaStatus] = useState<VAStatus>(null);
  const [vaDisabilityStatus, setVaDisabilityStatus] = useState<VADisabilityStatus>(null);
  const [vaDisabilityRatingPercentage, setVaDisabilityRatingPercentage] = useState<number>(50);
  const [desiredPiti, setDesiredPiti] = useState<number>(2500);
  const [interestRate, setInterestRate] = useState<number>(6.5);
  const [loanTerm, setLoanTerm] = useState<number>(30);
  const [downPaymentPercent, setDownPaymentPercent] = useState<number>(20);
  const [propertyTaxRate, setPropertyTaxRate] = useState<number>(2.0);
  const [annualInsurance, setAnnualInsurance] = useState<number>(1800);
  const [hoaFees, setHoaFees] = useState<number>(0);
  const [pmiRate, setPmiRate] = useState<number>(0.5);
  const [includePmi, setIncludePmi] = useState<boolean>(true);
  const [hasInitializedRate, setHasInitializedRate] = useState<boolean>(false);

  // Market data status
  const isUsingLiveRate = !!marketData;

  // Sync interest rate with live market data on initial load
  useEffect(() => {
    if (marketData && !hasInitializedRate) {
      setInterestRate(roundRate(getInterestRateForLoanType(loanType)));
      setHasInitializedRate(true);
    }
  }, [marketData, hasInitializedRate, getInterestRateForLoanType, loanType]);

  // Handle loan type change - snap down payment and interest rate to defaults
  const handleLoanTypeChange = (newLoanType: LoanTypeOption) => {
    setLoanType(newLoanType);
    const defaults = getDefaultValuesForLoanType(newLoanType, newLoanType === 'va');
    setDownPaymentPercent(defaults.downPaymentPercentage);
    
    // Use live rate if available, otherwise fall back to static default
    const liveRate = marketData 
      ? getInterestRateForLoanType(newLoanType) 
      : defaults.interestRate;
    setInterestRate(roundRate(liveRate));
    
    // Reset VA-specific fields when switching away from VA
    if (newLoanType !== 'va') {
      setVaStatus(null);
      setVaDisabilityStatus(null);
    } else {
      setVaStatus('activeDuty');
    }
    
    // Disable PMI for VA and USDA loans
    if (newLoanType === 'va' || newLoanType === 'usda') {
      setIncludePmi(false);
    }
  };

  // Handle VA status change
  const handleVAStatusChange = (status: VAStatus) => {
    setVaStatus(status);
    if (status !== 'separatedRetired') {
      setVaDisabilityStatus(null);
    }
  };

  // Handle VA disability status change
  const handleVADisabilityStatusChange = (status: VADisabilityStatus) => {
    setVaDisabilityStatus(status);
    // 100% VA disability = property tax exemption
    if (status === 'full') {
      setPropertyTaxRate(0);
    }
    // Partial disability - initialize slider
    if (status === 'partial') {
      setVaDisabilityRatingPercentage(50);
    }
  };

  // Handle VA disability percentage change (for partial ratings)
  const handleVADisabilityPercentageChange = (percentage: number) => {
    setVaDisabilityRatingPercentage(percentage);
    // Apply property tax reduction based on rating percentage
    const taxReductionFactor = percentage / 100;
    const baseTaxRate = 2.0;
    const reducedRate = baseTaxRate * (1 - taxReductionFactor);
    setPropertyTaxRate(Math.max(0, reducedRate));
  };

  // Calculate results
  const results = useMemo(() => {
    return calculateReversePiti({
      desiredPiti,
      interestRate,
      loanTerm,
      downPaymentPercent,
      propertyTaxRate,
      annualInsurance,
      hoaFees,
      pmiRate,
      includePmi
    });
  }, [desiredPiti, interestRate, loanTerm, downPaymentPercent, propertyTaxRate, annualInsurance, hoaFees, pmiRate, includePmi]);

  // Formatting utilities
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyDecimal = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format interest rate to 3 decimal places for display
  const formattedInterestRate = Number(interestRate.toFixed(3));

  // Reset to default values
  const resetToDefaults = useCallback(() => {
    setLoanType('conventional');
    setVaStatus(null);
    setVaDisabilityStatus(null);
    setVaDisabilityRatingPercentage(50);
    setDesiredPiti(2500);
    setInterestRate(6.5);
    setLoanTerm(30);
    setDownPaymentPercent(20);
    setPropertyTaxRate(2.0);
    setAnnualInsurance(1800);
    setHoaFees(0);
    setPmiRate(0.5);
    setIncludePmi(true);
    setHasInitializedRate(false);
  }, []);

  // Get exportable state for JSON export
  const getExportableState = useCallback(() => ({
    loanType,
    vaStatus,
    vaDisabilityStatus,
    vaDisabilityRatingPercentage,
    desiredPiti,
    interestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    includePmi,
  }), [
    loanType, vaStatus, vaDisabilityStatus, vaDisabilityRatingPercentage,
    desiredPiti, interestRate, loanTerm, downPaymentPercent,
    propertyTaxRate, annualInsurance, hoaFees, pmiRate, includePmi
  ]);

  // Load state from imported JSON
  const loadFromExport = useCallback((state: Record<string, any>) => {
    if (state.loanType) setLoanType(state.loanType);
    if (state.vaStatus !== undefined) setVaStatus(state.vaStatus);
    if (state.vaDisabilityStatus !== undefined) setVaDisabilityStatus(state.vaDisabilityStatus);
    if (state.vaDisabilityRatingPercentage !== undefined) setVaDisabilityRatingPercentage(state.vaDisabilityRatingPercentage);
    if (state.desiredPiti !== undefined) setDesiredPiti(state.desiredPiti);
    if (state.interestRate !== undefined) setInterestRate(state.interestRate);
    if (state.loanTerm !== undefined) setLoanTerm(state.loanTerm);
    if (state.downPaymentPercent !== undefined) setDownPaymentPercent(state.downPaymentPercent);
    if (state.propertyTaxRate !== undefined) setPropertyTaxRate(state.propertyTaxRate);
    if (state.annualInsurance !== undefined) setAnnualInsurance(state.annualInsurance);
    if (state.hoaFees !== undefined) setHoaFees(state.hoaFees);
    if (state.pmiRate !== undefined) setPmiRate(state.pmiRate);
    if (state.includePmi !== undefined) setIncludePmi(state.includePmi);
  }, []);

  return {
    // State
    loanType,
    vaStatus,
    vaDisabilityStatus,
    vaDisabilityRatingPercentage,
    desiredPiti,
    interestRate,
    formattedInterestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    includePmi,
    
    // Setters
    setDesiredPiti,
    setInterestRate: (rate: number) => setInterestRate(roundRate(rate)),
    setLoanTerm,
    setDownPaymentPercent,
    setPropertyTaxRate,
    setAnnualInsurance,
    setHoaFees,
    setPmiRate,
    setIncludePmi,
    
    // Handlers
    handleLoanTypeChange,
    handleVAStatusChange,
    handleVADisabilityStatusChange,
    handleVADisabilityPercentageChange,
    
    // Market data status
    isUsingLiveRate,
    
    // Computed
    results,
    
    // Utilities
    formatCurrency,
    formatCurrencyDecimal,
    
    // Reset
    resetToDefaults,
    
    // Export/Import
    getExportableState,
    loadFromExport,
  };
};
